import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import PhoneInput from '../../components/forms/phoneInput'
import { createCompany, updateCompany, getPlans } from '../../services/platform'
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse'
import refreshPlatform from './refreshPlatform'
import { toast } from '../../utils/toast'

/**
 * Create / edit a company from the platform panel.
 *
 * `company` null → create: company name, its admin (name + e-mail, who gets a
 * set-password mail), phone and plan. A company object → edit: name, phone
 * and plan. Choosing a plan with trial days starts that trial (server side).
 */
const CompanyFormModal = ({ company, onClose }) => {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const isEdit = !!company
    const currentPlanId = company?.plan?.id || company?.planId || ''

    const [form, setForm] = useState({
        name: company?.name || '',
        ownerName: '',
        email: '',
        phone: company?.phone || '',
        planId: currentPlanId,
    })
    const [plans, setPlans] = useState([])

    const { data: plansData } = useQuery('platform-plans', getPlans)
    useEffect(() => { onQuerySuccess(plansData, (r) => setPlans(r.plans || r || [])) }, [plansData])

    const done = () => {
        refreshPlatform(qc)
        onClose()
    }
    const createMut = useMutation(createCompany, { onSuccess: onMutateSuccess(done, t('platform.companies.createdToast')) })
    const updateMut = useMutation((payload) => updateCompany(company.id, payload), { onSuccess: onMutateSuccess(done, t('platform.companies.updatedToast')) })
    const isSaving = createMut.isLoading || updateMut.isLoading

    const selectedPlan = plans.find(p => p.id === form.planId)
    // Only a newly chosen plan restarts its trial; re-saving the same plan doesn't.
    const startsTrial = selectedPlan?.trialDays > 0 && form.planId !== currentPlanId

    const handleSubmit = (e) => {
        e.preventDefault()
        const name = form.name.trim()

        if (isEdit) {
            if (!name) return toast.error(t('platform.common.nameRequired'))
            updateMut.mutate({ name, phone: form.phone || null, planId: form.planId || null })
            return
        }

        if (!name || !form.ownerName.trim() || !form.email.trim()) return toast.error(t('platform.common.nameEmailRequired'))
        // The API creates the company and its admin user in one go; the admin's
        // username is derived from the e-mail, the phone (E.164) also sets the
        // company's default phone country.
        createMut.mutate({
            companyName: name,
            ownerFullName: form.ownerName.trim(),
            ownerEmail: form.email.trim(),
            ...(form.phone && { phone: form.phone }),
            ...(form.planId && { planId: form.planId }),
        })
    }

    return (
        <div className="p-modal-overlay" onClick={onClose}>
            <div className="p-modal" onClick={e => e.stopPropagation()}>
                <h3>{isEdit ? t('platform.companies.editTitle') : t('platform.companies.createTitle')}</h3>
                <form onSubmit={handleSubmit}>
                    <label>{t('platform.companies.nameLabel')}</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Cleaning Ltd" maxLength={200} />

                    {!isEdit && (
                        <>
                            <label>{t('platform.companies.adminNameLabel')}</label>
                            <input value={form.ownerName} onChange={e => setForm(f => ({ ...f, ownerName: e.target.value }))} placeholder="John Doe" />
                            <label>{t('platform.companies.adminEmailLabel')}</label>
                            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="admin@acme.com" />
                        </>
                    )}

                    <label>{t('platform.common.phoneLabel')}</label>
                    <PhoneInput name="phone" value={form.phone} onChange={(phone) => setForm(f => ({ ...f, phone }))} />

                    <label>{t('platform.companies.planLabel')}</label>
                    <select value={form.planId} onChange={e => setForm(f => ({ ...f, planId: e.target.value }))}>
                        <option value="">{t('platform.companies.noPlanOption')}</option>
                        {plans.map(p => (
                            <option key={p.id} value={p.id}>
                                {p.name}{p.price != null ? ` ${t('platform.companyDetail.perMonth', { price: '$' + Number(p.price).toFixed(2) })}` : ''}
                            </option>
                        ))}
                    </select>
                    {startsTrial && (
                        <p style={{ fontSize: 12, color: '#697586', margin: '6px 0 0' }}>{t('platform.companies.planTrialHint', { count: selectedPlan.trialDays })}</p>
                    )}

                    {!isEdit && <p style={{ fontSize: 12, color: '#697586', margin: '12px 0 0' }}>{t('platform.companies.adminInviteHint')}</p>}

                    <div className="p-modal-actions">
                        <button type="button" className="btn-sbr btn-ghost-sbr" onClick={onClose} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                        <button type="submit" className="btn-sbr btn-primary-sbr" disabled={isSaving} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>
                            {isEdit
                                ? (isSaving ? t('platform.common.saving') : t('platform.common.save'))
                                : (isSaving ? t('platform.common.creating') : t('platform.common.create'))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CompanyFormModal
